import { Dashboard } from "api/model/dashboard/Dashboard";
import { T } from "services/lang/LanguageService";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import React from "react";
import { handleAddNewTab } from "./ImportDashboardOrTab";

interface DashboardEditorProps {
  updateQuery: any;
  dashboard: Dashboard;
  setDashboard: (dashboard: Dashboard) => void;
  tabId: number;
  triggerTabConfiguratorVisibility: () => void;
  setReorderTabsDialogVisibility: (reorderTabsDialogVisibility: boolean) => void;
  setDashboardConfiguratorVisibility: (dashboardConfiguratorVisibility: boolean) => void;
  setIsImportDashboardDialogVisibility: (isImportDashboardDialogVisible: boolean) => void;
  setAddNewGroupDialogVisibility: (addNewGroupDialogVisibility: boolean) => void;
  isGroupReorderEnabled: boolean;
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => void;
  setIsCreateDatasetDialogVisible: (isCreateDatasetDialogVisible: boolean) => void;
  setDatasetSearchDialogVisibility: (isDatasetSearchDialogVisible: boolean) => void;
}

const DashboardEditor = (props: DashboardEditorProps) => {
  const isGridChanged = useDashboardStore(state => state.isGridChanged);
  const navigate = useNavigate();

  const removeTabConfirmation = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("action.remove-tab"),
      confirmedCallback: handleRemoveSelectedTab,
    });
  };

  const handleRemoveSelectedTab = () => {
    const updatedCurrentDashboard = {
      ...props.dashboard,
      dashboardTabsJs: [
        ...props.dashboard.dashboardTabsJs.slice(0, props.tabId),
        ...props.dashboard.dashboardTabsJs.slice(props.tabId + 1),
      ],
    };
    props.setDashboard(updatedCurrentDashboard);
    navigate(`/dashboard/${props.dashboard?.id}/tab/0`);
  };

  const handleSaveDashboard = () => {
    if (isGridChanged) {
      easasConfirmDialog({
        icon: "pi pi-exclamation-triangle",
        header: T("action.confirm-action"),
        message: T("confirm-dialog.dashboard-have-unsaved-grid-layout-changes"),
        confirmedCallback: () => {
          useDashboardStore.getState().triggerDashboardSave();
          props.updateQuery.mutate(props.dashboard);
        },
      });
    } else {
      props.updateQuery.mutate(props.dashboard);
    }
  };

  return (
    <div className="flex flex-row justify-content-between gap-5 overflow-x-auto">
      <div className="flex flex-row gap-8 mb-1">
        <div className="flex flex-column gap-2 buttons-group-container">
          <div className="w-max">{T("word.dashboard")}</div>
          <div className="flex flex-row gap-2 mb-3">
            <Button
              icon="pi pi-save"
              tooltip={T("action.save-dashboard")}
              label={T("action.save")}
              className="dashboard-editor-button icon-blue"
              tooltipOptions={{ position: "bottom" }}
              onClick={() => handleSaveDashboard()}
            />
            <Button
              icon="pi pi-cog"
              label={T("action.configure")}
              tooltip={T("action.configure-dashboard")}
              className="dashboard-editor-button icon-orange"
              onClick={() => props.setDashboardConfiguratorVisibility(true)}
              tooltipOptions={{ position: "bottom" }}
            />
            <Button
              tooltip={T("action.import-dashboard-or-tab")}
              label={T("action.import")}
              icon="pi pi-file-import"
              tooltipOptions={{ position: "bottom" }}
              className="dashboard-editor-button icon-green"
              onClick={() => props.setIsImportDashboardDialogVisibility(true)}
            />
          </div>
        </div>
        <div className="flex flex-column gap-2">
          <div className="w-max">{T("word.tabs")}</div>
          <div className="flex flex-row gap-2">
            <Button
              icon="pi pi-plus"
              label={T("action.add")}
              tooltip={T("action.add-new-tab")}
              className="dashboard-editor-button icon-green"
              tooltipOptions={{ position: "bottom" }}
              onClick={() => handleAddNewTab(props.dashboard, props.setDashboard, "Tab")}
            />
            <Button
              icon="pi pi-cog"
              label={T("action.configure")}
              tooltip={T("action.configure-tab")}
              className="dashboard-editor-button icon-orange"
              onClick={() => props.triggerTabConfiguratorVisibility()}
              tooltipOptions={{ position: "bottom" }}
            />
            <Button
              tooltip={T("label.reorder-tabs")}
              label={T("action.reorder")}
              icon="pi pi-arrow-right-arrow-left"
              className="dashboard-editor-button"
              onClick={() => props.setReorderTabsDialogVisibility(true)}
              tooltipOptions={{ position: "bottom" }}
            />
            <Button
              icon="pi pi-trash"
              label={T("action.remove")}
              tooltip={T("action.remove-tab")}
              tooltipOptions={{ position: "bottom" }}
              className="dashboard-editor-button icon-red"
              onClick={removeTabConfirmation}
            />
          </div>
        </div>
        <div className="flex flex-column gap-2">
          <div className="w-max">{T("label.component-groups")}</div>
          <div className="flex flex-row justify-content-end gap-2">
            <Button
              tooltip={T("action.add-new-components-group")}
              label={T("action.add")}
              icon="pi pi-plus"
              className="dashboard-editor-button icon-green"
              tooltipOptions={{ position: "bottom" }}
              onClick={() => props.setAddNewGroupDialogVisibility(true)}
            />
            <Button
              tooltip={T("action.reorder-components-groups")}
              label={T("action.reorder")}
              icon="pi pi-arrow-right-arrow-left"
              tooltipOptions={{ position: "bottom" }}
              className="dashboard-editor-button"
              onClick={() => props.setIsGroupReorderEnabled(!props.isGroupReorderEnabled)}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-column gap-2">
        <div className="w-max">{T("word.datasets")}</div>
        <div className="flex flex-row gap-2">
          <Button
            icon="pi pi-plus"
            label={T("action.create")}
            tooltip={T("action.create-new-dataset")}
            className="dashboard-editor-button icon-green"
            tooltipOptions={{ position: "bottom" }}
            onClick={() => props.setIsCreateDatasetDialogVisible(true)}
          />
          <Button
            icon="pi pi-search"
            label={T("action.search")}
            tooltip={T("action.search-dataset")}
            className="dashboard-editor-button icon-orange"
            tooltipOptions={{ position: "bottom" }}
            onClick={() => props.setDatasetSearchDialogVisibility(true)}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardEditor;
